interface NavigationHistoryEntry {
  from: 'delivery' | 'kit';
  uuid: string;
  timestamp: number;
}

class NavigationHistoryService {
  private history: Map<string, NavigationHistoryEntry> = new Map();
  private readonly CLEANUP_INTERVAL = 30 * 60 * 1000; // 30 minutes
  private readonly MAX_AGE = 60 * 60 * 1000; // 1 hour

  constructor() {
    // Clean up old entries periodically
    setInterval(() => {
      this.cleanup();
    }, this.CLEANUP_INTERVAL);
  }

  /**
   * Store navigation history when navigating TO a delivery item
   * @param itemUuid The UUID of the delivery item being navigated to
   * @param from The type of source ('delivery' or 'kit')
   * @param sourceUuid The UUID of the source (delivery or kit)
   */
  storeNavigation(itemUuid: string, from: 'delivery' | 'kit', sourceUuid: string): void {
    try {
      this.history.set(itemUuid, {
        from,
        uuid: sourceUuid,
        timestamp: Date.now()
      });
      console.log(`NavigationHistory: Stored navigation for item ${itemUuid} from ${from} ${sourceUuid}`);
    } catch (error) {
      console.error('NavigationHistory: Error storing navigation:', error);
    }
  }

  /**
   * Get the navigation history for a delivery item
   * @param itemUuid The UUID of the delivery item
   * @returns The navigation history entry or null if not found/expired
   */
  getNavigation(itemUuid: string): NavigationHistoryEntry | null {
    try {
      const entry = this.history.get(itemUuid);
      
      if (!entry) {
        console.log(`NavigationHistory: No history found for item ${itemUuid}`);
        return null;
      }

      // Check if entry is too old
      if (Date.now() - entry.timestamp > this.MAX_AGE) {
        console.log(`NavigationHistory: History expired for item ${itemUuid}`);
        this.history.delete(itemUuid);
        return null;
      }

      console.log(`NavigationHistory: Retrieved history for item ${itemUuid}: ${entry.from} ${entry.uuid}`);
      return entry;
    } catch (error) {
      console.error('NavigationHistory: Error getting navigation:', error);
      return null;
    }
  }

  /**
   * Clear navigation history for a specific item
   * @param itemUuid The UUID of the delivery item
   */
  clearNavigation(itemUuid: string): void {
    try {
      this.history.delete(itemUuid);
      console.log(`NavigationHistory: Cleared history for item ${itemUuid}`);
    } catch (error) {
      console.error('NavigationHistory: Error clearing navigation:', error);
    }
  }

  /**
   * Clean up old entries
   */
  private cleanup(): void {
    try {
      const now = Date.now();
      const toDelete: string[] = [];

      this.history.forEach((entry, itemUuid) => {
        if (now - entry.timestamp > this.MAX_AGE) {
          toDelete.push(itemUuid);
        }
      });

      toDelete.forEach(itemUuid => {
        this.history.delete(itemUuid);
      });

      if (toDelete.length > 0) {
        console.log(`NavigationHistory: Cleaned up ${toDelete.length} expired entries`);
      }
    } catch (error) {
      console.error('NavigationHistory: Error during cleanup:', error);
    }
  }

  /**
   * Get the size of the history map (for debugging)
   */
  getHistorySize(): number {
    return this.history.size;
  }
}

// Export a singleton instance
export default new NavigationHistoryService();
